import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Users, Calendar, Ticket } from "lucide-react";
import { eventosPublicosApi, SucursalBranding } from "../../lib/api";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

interface CheckinInfo extends SucursalBranding {
  id: number;
  nombre: string;
  cantidadEntradas: number;
  estado: string;
  yaUsada: boolean;
  checkInAt?: string | null;
  eventoTitulo?: string;
  eventoFecha?: string | null;
}

export default function EventoCheckinPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CheckinInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    eventosPublicosApi.checkIn(token)
      .then((d) => {
        setData(d);
        aplicarFaviconPagina(d.logoUrl);
        aplicarTituloPagina(d.sucursalNombre ?? d.eventoTitulo);
      })
      .catch((e) => setError(e?.message ?? "Entrada no válida"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-400">Verificando entrada…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
          <AlertCircle className="size-12 text-red-400 mx-auto mb-3" />
          <h2 className="font-bold text-lg text-gray-800 mb-2">Entrada no válida</h2>
          <p className="text-gray-500 text-sm">{error ?? "El código QR no es válido."}</p>
        </div>
      </div>
    );
  }

  const color = data.yaUsada ? "#F59E0B" : "#10B981";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full overflow-hidden">
        <div className="text-white px-5 py-6 text-center" style={{ backgroundColor: color }}>
          {data.yaUsada ? <AlertCircle className="size-10 mx-auto mb-2" /> : <CheckCircle2 className="size-10 mx-auto mb-2" />}
          <h1 className="text-xl font-bold">{data.yaUsada ? "Entrada ya utilizada" : "¡Entrada válida!"}</h1>
          {data.checkInAt && (
            <p className="text-sm opacity-90 mt-1">
              {data.yaUsada ? "Usada" : "Registrada"} a las {new Date(data.checkInAt).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <Ticket className="size-5 text-gray-400 shrink-0" />
            <div>
              <p className="font-bold text-gray-900">{data.nombre}</p>
              <p className="text-sm text-gray-500">{data.eventoTitulo}</p>
            </div>
          </div>
          {data.eventoFecha && (
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <Calendar className="size-4 text-gray-400 shrink-0" />
              {new Date(data.eventoFecha.slice(0, 10) + "T00:00:00").toLocaleDateString("es-VE", { day: "2-digit", month: "long" })}
            </div>
          )}
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <Users className="size-4 text-gray-400 shrink-0" />
            {data.cantidadEntradas} entrada(s)
          </div>
        </div>
        {data.yaUsada && (
          <div className="px-5 py-3 bg-amber-50 border-t border-amber-100 text-xs text-amber-700 text-center">
            Esta entrada ya fue escaneada antes. Verificá identidad antes de dejar pasar.
          </div>
        )}
      </div>
    </div>
  );
}
